import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import { useNavigate } from 'react-router-dom';
import { supplierAccountService } from '../../services/supplierAccountService';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/ui/Icon';
import { Checkbox } from '../../components/ui/Checkbox';
import Footer from '../homepage/components/Footer';

export default function SupplierSignup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [authSession, setAuthSession] = useState(null);
  
  // Auth data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  // Business data
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [fulfillmentCapacity, setFulfillmentCapacity] = useState('');
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);

  const specializations = [
    '3D Printing',
    'CNC Machining',
    'Electronics Assembly',
    'Laser Cutting',
    'Injection Molding',
    'PCB Manufacturing',
    'Powder Coating',
    'Quality Inspection',
    'Packaging & Shipping'
  ];

  const handleAuthSignup = async (e) => {
    e?.preventDefault();
    setError('');
    
    // Just validate and move to next step - don't create account yet
    if (!firstName || !lastName || !email || !password) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate password strength
    if (password?.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Move to next step - account creation will happen in step 2
    setBusinessEmail(email); // Pre-fill with signup email
    setStep(2);
  };

  const handleApplicationSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!businessName || !businessEmail) {
        throw new Error('Please fill in all required fields');
      }

      if (selectedSpecializations?.length === 0) {
        throw new Error('Please select at least one specialization');
      }

      // Step 1: Create auth account
      console.log('Creating supplier auth account...');
      const { data: signupData, error: signupError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            role: 'supplier'
          },
          emailRedirectTo: undefined, // Disable email confirmation redirect
        }
      });

      if (signupError) throw signupError;
      
      if (!signupData?.user) {
        throw new Error('User creation failed - no user data returned');
      }
      
      console.log('✅ Supplier auth account created:', signupData?.user?.id);
      
      // Step 2: Try to sign in (if email confirmation is disabled, this will work)
      let session = signupData?.session;
      
      if (!session) {
        // Try to sign in
        try {
          const { data: signinData, error: signinError } = await supabase?.auth?.signInWithPassword({
            email,
            password
          });

          if (signinError && signinError?.message?.includes('email_not_confirmed')) {
            // Email confirmation required - show helpful message
            throw new Error('Account created! Please check your email to confirm your account before we can submit your application. After confirming, you can log in and submit your supplier application.');
          }

          if (signinError) throw signinError;
          
          session = signinData?.session;
        } catch (signinErr) {
          // If signin fails, we'll still try to show success message
          // The user can confirm email and login later to submit application
          console.warn('Could not sign in immediately:', signinErr);
          if (signinErr?.message?.includes('email_not_confirmed')) {
            throw signinErr; // Re-throw email confirmation error
          }
        }
      }

      // Step 3: Create supplier account (only if we have a session)
      if (session) {
        setAuthSession(session);
        
        const accountData = {
          businessName,
          businessEmail,
          businessPhone,
          fulfillmentCapacity: parseInt(fulfillmentCapacity) || 0,
          specialization: selectedSpecializations
        };

        console.log('Creating supplier account...');
        await supplierAccountService?.createAccount(accountData);
        console.log('✅ Supplier account created successfully');
        
        // Build success message
        let message = 'Your supplier application has been submitted successfully! ';
        message += 'Our admin team will review your application and contact you via email. ';
        message += 'You will be notified once your account is approved.';
        
        setSuccessMessage(message);
        setError('');
        setStep(3);
      } else {
        // No session - account created but email needs confirmation
        throw new Error('Account created successfully! Please check your email to confirm your account. After confirming, you can log in and submit your supplier application.');
      }
    } catch (err) {
      console.error('Error in supplier signup flow:', err);
      setError(err?.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialization = (spec) => {
    setSelectedSpecializations(prev =>
      prev?.includes(spec)
        ? prev?.filter(s => s !== spec)
        : [...prev, spec]
    );
  };

  return (
    <>
      {/* Header */}
      <Header />

      <div className="min-h-screen bg-gray-50 pt-36 pb-12 px-4 sm:px-6 lg:px-8 mt-10">
        <div className="max-w-2xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
              {[1, 2, 3]?.map((num) => (
                <div key={num} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    step >= num ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {num}
                  </div>
                  {num < 3 && (
                    <div className={`w-16 h-1 mx-2 ${
                      step > num ? 'bg-purple-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-4 space-x-20 text-sm">
              <span className={step >= 1 ? 'text-purple-600 font-medium' : 'text-gray-500'}>
                Account
              </span>
              <span className={step >= 2 ? 'text-purple-600 font-medium' : 'text-gray-500'}>
                Business Info
              </span>
              <span className={step >= 3 ? 'text-purple-600 font-medium' : 'text-gray-500'}>
                Confirmation
              </span>
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-white rounded-lg shadow-md p-8">
            {/* Step 1: Account Creation */}
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Apply for Partnership</h2>
                <p className="text-gray-600 mb-6">Create your account to start the supplier application process</p>
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}
                <form onSubmit={handleAuthSignup} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e?.target?.value)}
                      required
                      placeholder="John"
                    />
                    <Input
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e?.target?.value)}
                      required
                      placeholder="Doe"
                    />
                  </div>
                  <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    required
                    placeholder="supplier@example.com"
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    required
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full"
                    iconName={loading ? "Loader2" : "ArrowRight"}
                    iconClassName={loading ? "animate-spin" : ""}
                    iconPosition="right"
                  >
                    {loading ? 'Creating Account...' : 'Continue'}
                  </Button>
                </form>
                <p className="mt-4 text-sm text-center text-gray-600">
                  Already have an account?{' '}
                  <button
                    onClick={() => navigate('/signin')}
                    className="text-purple-600 hover:text-purple-700 font-medium"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            )}

            {/* Step 2: Business Information */}
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Supplier Application</h2>
                <p className="text-gray-600 mb-6">Tell us about your business capabilities</p>
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}
                <form onSubmit={handleApplicationSubmit} className="space-y-6">
                  <Input
                    label="Business Name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e?.target?.value)}
                    required
                    placeholder="Your Business Name"
                  />
                  <Input
                    label="Business Email"
                    type="email"
                    value={businessEmail}
                    onChange={(e) => setBusinessEmail(e?.target?.value)}
                    required
                    placeholder="contact@business.com"
                  />
                  <Input
                    label="Business Phone (Optional)"
                    type="tel"
                    value={businessPhone}
                    onChange={(e) => setBusinessPhone(e?.target?.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                  <Input
                    label="Monthly Fulfillment Capacity"
                    type="number"
                    value={fulfillmentCapacity}
                    onChange={(e) => setFulfillmentCapacity(e?.target?.value)}
                    placeholder="e.g., 1000"
                    helperText="How many orders can you handle per month?"
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Specializations <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {specializations?.map((spec) => (
                        <div
                          key={spec}
                          onClick={() => toggleSpecialization(spec)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedSpecializations?.includes(spec)
                              ? 'border-purple-600 bg-purple-50' :'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <Checkbox
                              checked={selectedSpecializations?.includes(spec)}
                              onChange={() => toggleSpecialization(spec)}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">{spec}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex">
                      <Icon name="AlertCircle" size={20} className="text-yellow-600 mr-2 flex-shrink-0" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium mb-1">Application Review Process</p>
                        <p>Your application will be reviewed by our admin team. Once approved, you will receive an email notification and can start accepting fulfillment orders.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <Button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1"
                      iconName="ArrowLeft"
                      iconPosition="left"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1"
                      iconName={loading ? "Loader2" : "Send"}
                      iconClassName={loading ? "animate-spin" : ""}
                      iconPosition="right"
                    >
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 3: Success Message */}
            {step === 3 && (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Icon name="CheckCircle" size={32} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-6 text-left">
                  <p className="text-gray-700">{successMessage}</p>
                </div>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/')}
                    className="w-full"
                    iconName="Home"
                    iconPosition="left"
                  >
                    Return to Homepage
                  </Button>
                  <Button
                    onClick={() => navigate('/signin')}
                    className="w-full"
                    iconName="LogIn"
                    iconPosition="left"
                  >
                    Sign In to Your Account
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
}